import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Table } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@shared/contexts";

import type {
  AdminStructureFilters,
  ListAdminStructuresResponseItem,
  StructureOperationalStatus,
  StructureType,
} from "../../api/structures";
import type { AdminStructureFilterData } from "../../schemas";
import { AdminStructuresFilter } from "./AdminStructuresFilter";
import { AdminStructuresDetailsAction } from "./AdminStructuresDetailsAction";
import AdminStructureDetailModal from "./AdminStructureDetailModal";

interface AdminStructuresTabProps {
  complexId: string;
  structures: ListAdminStructuresResponseItem[];
  isLoading?: boolean;
  onFilter: (filters: Partial<AdminStructureFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<AdminStructureFilterData>;
  paginatedMeta?: PaginationMeta;
}

const AdminStructuresTab = ({
  complexId,
  structures,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: AdminStructuresTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string | null>(
    null,
  );

  const canWrite = me?.role !== "ownerViewer";

  const handleOpenDetails = useCallback((row: ListAdminStructuresResponseItem) => {
    setSelectedStructureId(String(row.id));
    setModalOpen(true);
  }, []);

  const tableData = useMemo(
    () =>
      structures.map((s) => ({
        ...s,
        type: s.type
          ? translate(`admin.structures.structureType.${s.type as StructureType}`)
          : "",
        operationalStatus: s.operationalStatus
          ? translate(
              `admin.structures.operationalStatus.${s.operationalStatus as StructureOperationalStatus}`,
            )
          : "",
        coordinateX: s.coordinateX ?? "",
        coordinateY: s.coordinateY ?? "",
        detailsAction: (
          <AdminStructuresDetailsAction
            key={s.id}
            rowData={s}
            onDetails={handleOpenDetails}
          />
        ),
      })),
    [structures, translate, handleOpenDetails],
  );

  return (
    <div className="p-4">
      <Table<ListAdminStructuresResponseItem>
        userId={me?.id ?? ""}
        tableId="admin-structures-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableFilter={() => (
          <AdminStructuresFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate("admin.structures.table.filterButtonText")}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "name",
            label: translate("admin.structures.table.column.name"),
            minWidth: "200px",
            sortType: "text",
          },
          {
            key: "type",
            label: translate("admin.structures.table.column.type"),
            minWidth: "140px",
            sortType: "text",
          },
          {
            key: "operationalStatus",
            label: translate("admin.structures.table.column.operationalStatus"),
            minWidth: "160px",
            sortType: "text",
          },
          {
            key: "coordinateX",
            label: translate("admin.structures.table.column.coordinateX"),
            minWidth: "140px",
            sortType: "text",
          },
          {
            key: "coordinateY",
            label: translate("admin.structures.table.column.coordinateY"),
            minWidth: "140px",
            sortType: "text",
          },
          {
            key: "detailsAction",
            label: translate("admin.structures.table.column.actions"),
            minWidth: "110px",
          },
        ]}
        tableData={
          tableData as { [key: string]: unknown; color?: string }[]
        }
      />

      <AdminStructureDetailModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedStructureId(null);
        }}
        complexId={complexId}
        structureId={selectedStructureId}
        canWrite={canWrite}
      />
    </div>
  );
};

export default AdminStructuresTab;
